import React from "react";

import { ArrowRight, Loader2 } from "lucide-react";

import Button from "../../Components/Button";
import Modal from "../../Components/Modal";
import type { ReviewAssessment } from "../../Store/Reducers/AssessmentSlice";
import { formatAssessmentDateTimePartsUtc } from "../../Utils/Helper/assessmentDateUtils";
import { normalizeTeamAssessmentDisplayName } from "../../Utils/Helper/assessmentOriginHelpers";

export type PendingFormalAssessmentRow = {
  assessment: ReviewAssessment;
  accountId: string;
  accountName: string;
};

type PendingFormalAssessmentsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  loading?: boolean;
  error?: string | null;
  rows: PendingFormalAssessmentRow[];
  onAssess: (row: PendingFormalAssessmentRow) => void;
};

const PendingFormalAssessmentsModal: React.FC<
  PendingFormalAssessmentsModalProps
> = ({ isOpen, onClose, loading = false, error = null, rows, onAssess }) => {
  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      title="Pending Formal Assessments"
      subtitle={
        <span className="text-sm text-gray-500">
          Select a formal assessment to complete before using Team Assess.
        </span>
      }
      size="4xl"
      contentClassName="overflow-hidden flex flex-col"
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="max-h-[28rem] overflow-auto rounded-lg border border-gray-200">
          <table className="min-w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="border-b border-gray-200">
                {["Assessment Title", "Created By", "Due Date", ""].map(
                  header => (
                    <th
                      key={header || "actions"}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      {header}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center">
                    <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading formal assessments...
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-12 text-center text-sm text-gray-500"
                  >
                    No pending formal assessments found
                  </td>
                </tr>
              ) : (
                rows.map(row => (
                  <tr
                    key={`${row.assessment.assessment_id}-${row.accountId}`}
                    className="transition-colors hover:bg-gray-50/80"
                  >
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                      {normalizeTeamAssessmentDisplayName(
                        row.assessment.assessment_name,
                        row.assessment.assessment_name || "Untitled Assessment"
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {row.assessment.created_by || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {row.assessment.due_date
                        ? formatAssessmentDateTimePartsUtc(
                            row.assessment.due_date,
                            "DMY_SHORT"
                          ).date
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        type="button"
                        variant="outline-primary"
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        onClick={() => onAssess(row)}
                      >
                        Assess
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};

export default PendingFormalAssessmentsModal;
